import type { ReactNode, TdHTMLAttributes, ThHTMLAttributes } from 'react';

import { cn } from '@/lib/cn';

export interface DataTableColumn<T> {
  key: string;
  header: ReactNode;
  render: (row: T) => ReactNode;
  /** Right-align (row actions, the last column, per *Basis layout*). */
  align?: 'start' | 'end';
}

/** A table with a horizontal-scroll wrapper for narrow screens — vervangt
 * `.data-table`. Give it `columns` + `rows`, or build the `<table>` by hand
 * with the exported `Th`/`Td`. */
export function DataTable<T>({
  columns,
  rows,
  rowKey,
  className,
}: {
  columns: DataTableColumn<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  className?: string;
}) {
  return (
    <div className={cn('overflow-x-auto', className)}>
      <table className="w-full border-collapse">
        <thead>
          <tr>
            {columns.map((column) => (
              <Th key={column.key} align={column.align ?? 'start'}>
                {column.header}
              </Th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={rowKey(row)} className="hover:bg-mint-tint">
              {columns.map((column) => (
                <Td key={column.key} align={column.align ?? 'start'}>
                  {column.render(row)}
                </Td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function Th({
  children,
  align = 'start',
  className,
  ...rest
}: Omit<ThHTMLAttributes<HTMLTableCellElement>, 'align'> & { align?: 'start' | 'end' }) {
  return (
    <th
      className={cn(
        'border-line text-accent h-12 border-b px-3 text-xs font-normal whitespace-nowrap uppercase',
        align === 'start' ? 'text-left' : 'text-right',
        className,
      )}
      {...rest}
    >
      {children}
    </th>
  );
}

export function Td({
  children,
  align = 'start',
  className,
  ...rest
}: Omit<TdHTMLAttributes<HTMLTableCellElement>, 'align'> & { align?: 'start' | 'end' }) {
  return (
    <td
      className={cn(
        'border-ink/10 text-ink h-14 border-b px-3 text-sm',
        align === 'start' ? 'text-left' : 'text-right',
        className,
      )}
      {...rest}
    >
      {children}
    </td>
  );
}
